import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Loader2, Send } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COPY = {
  de: {
    badge: "Personal in 48h",
    title: "Personal in 48 Stunden",
    subtitle: "Hinterlassen Sie Ihre Daten – unser Team meldet sich innerhalb von 48 Stunden.",
    name: "Ihr Name",
    phone: "Telefonnummer",
    city: "Stadt / Region",
    submit: "Anfrage senden",
    success: "Vielen Dank! Wir melden uns innerhalb von 48 Stunden.",
    error: "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    privacy: "Mit dem Absenden stimmen Sie unseren Datenschutzbestimmungen zu.",
  },
  en: {
    badge: "Staff in 48h",
    title: "Qualified staff in 48 hours",
    subtitle: "Leave your details – our team will get back to you within 48 hours.",
    name: "Your name",
    phone: "Phone number",
    city: "City / region",
    submit: "Send request",
    success: "Thank you! We will get back to you within 48 hours.",
    error: "An error occurred. Please try again.",
    privacy: "By submitting you agree to our privacy policy.",
  },
  hr: {
    badge: "Radnici u 48h",
    title: "Kvalitetni radnici u 48 sati",
    subtitle: "Ostavite podatke – javljamo se u roku od 48 sati.",
    name: "Vaše ime",
    phone: "Telefonski broj",
    city: "Grad / regija",
    submit: "Pošalji upit",
    success: "Hvala! Javljamo se u roku od 48 sati.",
    error: "Došlo je do greške. Pokušajte ponovno.",
    privacy: "Slanjem prihvaćate naša pravila privatnosti.",
  },
  hu: {
    badge: "Munkaerő 48h alatt",
    title: "Képzett munkaerő 48 órán belül",
    subtitle: "Hagyja meg adatait – csapatunk 48 órán belül jelentkezik.",
    name: "Az Ön neve",
    phone: "Telefonszám",
    city: "Város / régió",
    submit: "Kérés elküldése",
    success: "Köszönjük! 48 órán belül jelentkezünk.",
    error: "Hiba történt. Próbálja újra.",
    privacy: "Beküldéssel elfogadja adatvédelmi szabályzatunkat.",
  },
};

const DELAY_MS = 30000;
const DISMISS_KEY = "gwp_quicklead_dismissed";

export const QuickLead = () => {
  const { lang } = useLang();
  const copy = COPY[lang] || COPY.de;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", city: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = window.sessionStorage.getItem(DISMISS_KEY);
    if (dismissed === "1") return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setOpen(false);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact({
        name: form.name,
        email: `quicklead+${Date.now()}@genius-workpa.com`,
        company: form.city || null,
        phone: form.phone,
        message:
          `[QUICK LEAD – ${copy.badge}]\n` +
          `Name: ${form.name}\nTelefon: ${form.phone}\nStadt/Region: ${form.city}\n\n` +
          `Bitte innerhalb von 48 Stunden kontaktieren.`,
        gdpr_consent: true,
        language: lang,
      });
      toast.success(copy.success);
      setForm({ name: "", phone: "", city: "" });
      dismiss();
    } catch (err) {
      console.error(err);
      toast.error(copy.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.5, ease: [0.2, 0.6, 0.2, 1] }}
          data-testid="quick-lead-widget"
          className="fixed bottom-5 left-5 right-5 z-[60] mx-auto max-w-sm sm:left-auto sm:right-5"
        >
          <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#4C1D95] p-6 text-white shadow-glow-purple backdrop-blur">
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[#7C3AED]/40 blur-3xl" />
            <button
              onClick={dismiss}
              data-testid="quick-lead-close"
              aria-label="Close"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/80 transition-all hover:bg-white/20 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4B5FD]">
                <Sparkles className="h-3 w-3" />
                {copy.badge}
              </span>
              <h3 className="mt-3 font-display text-xl font-bold leading-tight tracking-tight">
                {copy.title}
              </h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-300">
                {copy.subtitle}
              </p>

              <form onSubmit={submit} className="mt-4 space-y-2.5">
                <Input
                  required
                  data-testid="quick-lead-name"
                  placeholder={copy.name}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-10 rounded-xl border-white/15 bg-white/10 text-sm text-white placeholder:text-white/50 focus-visible:ring-[#A78BFA]"
                />
                <Input
                  required
                  type="tel"
                  data-testid="quick-lead-phone"
                  placeholder={copy.phone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-10 rounded-xl border-white/15 bg-white/10 text-sm text-white placeholder:text-white/50 focus-visible:ring-[#A78BFA]"
                />
                <Input
                  required
                  data-testid="quick-lead-city"
                  placeholder={copy.city}
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="h-10 rounded-xl border-white/15 bg-white/10 text-sm text-white placeholder:text-white/50 focus-visible:ring-[#A78BFA]"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="quick-lead-submit"
                  className="!mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-white !text-[#0F172A] font-semibold shadow-glow-blue transition-all hover:scale-[1.02] hover:bg-slate-100"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {copy.submit}
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              <p className="mt-3 text-[10px] leading-snug text-white/50">{copy.privacy}</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuickLead;
